import type { PublicPortfolioTaskResource } from "api/generated";
import type {
  PublicLibraryTask,
  PublicLibraryTaskAssessmentModel,
  PublicLibraryTaskAttachment,
  PublicLibraryTaskMeta,
  PublicLibraryTaskSkill,
  PublicLibraryTaskSource,
  PublicLibraryTaskTag,
  PublicLibraryTaskUser,
} from "portfolio/types";
import { taskQuestionFromDto } from "./taskQuestion.mapper";

const mapUser = (
  user: PublicPortfolioTaskResource["user"],
): PublicLibraryTaskUser | null => {
  if (user === null || user === undefined) {
    return null;
  }

  return {
    id: user.id,
    fullName: user.full_name,
    shortage: user.shortage ?? null,
    profileImage: user.profile_image ?? null,
  };
};

const mapTag = (
  tag: PublicPortfolioTaskResource["tags"][number],
): PublicLibraryTaskTag => ({
  id: tag.id,
  parentId: tag.parent_id,
  name: tag.name,
});

const mapSkill = (
  skill: PublicPortfolioTaskResource["skills"][number],
): PublicLibraryTaskSkill => ({
  id: skill.id,
  name: skill.name,
  description: skill.description,
  categoryId: skill.category_id ?? null,
  educationLevels: skill.education_levels,
});

const mapSource = (
  source: PublicPortfolioTaskResource["sources"][number],
): PublicLibraryTaskSource => ({
  id: source.id,
  name: source.name,
});

const mapAttachment = (
  attachment: PublicPortfolioTaskResource["attachments"][number],
): PublicLibraryTaskAttachment => {
  const isVideo = attachment.is_video;
  const isCompleted = attachment.transcode_status === "completed";
  const hasStreamUrl = Boolean(attachment.stream_url);

  return {
    id: attachment.id,
    name: attachment.name,
    attachment: attachment.attachment,
    url: attachment.url,
    previewUrl: attachment.preview_url ?? null,
    mimetype: attachment.mimetype,
    is_video: isVideo,
    transcode_status: attachment.transcode_status,
    stream_url: attachment.stream_url,
    thumbnail_url: attachment.thumbnail_url,
    isPlayableVideo: isVideo && isCompleted && hasStreamUrl,
    isPendingOrFailedVideo:
      isVideo && (isCompleted === false || hasStreamUrl === false),
  };
};

const mapAssessmentModel = (
  model: PublicPortfolioTaskResource["assessment_models"][number],
): PublicLibraryTaskAssessmentModel => ({
  value: model.value,
  label: model.label,
});

const mapPublicLibraryTaskMeta = (
  meta: PublicPortfolioTaskResource["meta"],
): PublicLibraryTaskMeta => ({
  canUpdate: meta.can_update,
  canDelete: meta.can_delete,
  viewsAmount: meta.views_amount,
  portfolioTasksAmount: meta.portfolio_tasks_amount,
  portfolioTaskTemplatesAmount: meta.portfolio_task_templates_amount,
});

export const publicLibraryTaskFromDto = (
  resource: PublicPortfolioTaskResource,
): PublicLibraryTask => ({
  id: resource.id,
  user: mapUser(resource.user),
  createdBy: resource.created_by ?? null,
  school: resource.school ?? null,
  type: resource.type,
  templateName: resource.template_name,
  title: resource.title,
  description: resource.description,
  teacherInstructions: resource.teacher_instructions ?? null,
  bannerImage: resource.banner_image,
  tagIds: resource.tag_ids,
  tags: resource.tags.map(mapTag),
  skillIds: resource.skill_ids,
  skills: resource.skills.map(mapSkill),
  sourceIds: resource.source_ids,
  sources: resource.sources.map(mapSource),
  lobCompetenceIds: resource.lob_competence_ids,
  lobCompetences: resource.lob_competences.map((competence) => ({
    id: competence.id,
    name: competence.name,
  })),
  attachmentRequired: resource.attachment_required,
  attachments: resource.attachments.map(mapAttachment),
  subjects: resource.subjects,
  educationLevels: resource.education_levels,
  allowTaskRatings: resource.allow_task_ratings,
  assessmentModels: resource.assessment_models.map(mapAssessmentModel),
  questions: resource.questions.map(taskQuestionFromDto),
  createdAt: resource.created_at ? new Date(resource.created_at) : null,
  updatedAt: resource.updated_at ? new Date(resource.updated_at) : null,
  meta: mapPublicLibraryTaskMeta(resource.meta),
});
